/**
 * Phase 86 Plan 86-03 — the "Compost" design's single locked palette mapping.
 *
 * The handoff (`.planning/design/mytasks-compost/README.md`, "Design tokens")
 * lists final hex values next to the `ms-*` token they correspond to. Most
 * match closely but not exactly — the handoff's neutral hexes (page surface,
 * border, hairline, band tint, muted text) each differ from the live token
 * by a few points. Per the CONTEXT-locked resolution:
 *
 *   - Use the existing Tailwind token class wherever a token exists (page
 *     surface, raised surface, band/zebra tint, border, hairline, strong
 *     text, body text, muted text, accent). In particular the muted-text
 *     TOKEN (`--ms-text-muted: #6B6B75`) is used instead of the handoff's
 *     muted hex (`#8C8880`) — the token is darker, so it satisfies the
 *     handoff's own "meaning-carrying muted text never lighter than
 *     #8C8880" contrast rule with room to spare.
 *   - Use a raw hex only where no token exists: the zone colours (fresh /
 *     stale / graveyard, each with dark + tint(+border) variants), the
 *     9px micro-label grey, the dashed-border grey, the accent tint behind
 *     the active rail row and the cursor row, and the unfilled meter block
 *     grey. Named by role, not by colour, so a future theme change is a
 *     one-file edit.
 *
 * `MtRotMeter`, `MtZoneBand`, `MtActionChip`, `MyTaskRow` and the 86-04/86-05
 * rail + bands all read this one table — never restate a hex inline.
 */
import type { TaskRotZone } from '../../utils/format';

// ---- Tailwind token classes (prefer these over the raw hexes below) ----

export const TOKEN_CLASS = {
  pageSurface: 'bg-ms-canvas',
  raisedSurface: 'bg-ms-surface',
  bandTint: 'bg-ms-surface-2',
  zebraTint: 'bg-ms-surface-2/30',
  border: 'border-ms-border',
  hairline: 'border-ms-border-subtle',
  textStrong: 'text-ms-text-strong',
  textBody: 'text-ms-text',
  textMuted: 'text-ms-text-muted',
  accentText: 'text-ms-orange',
  accentBorder: 'border-ms-orange',
} as const;

// ---- Raw hexes — no ms-* token exists for these (verified during planning) ----

export const FRESH_HEX = '#10B981';
export const FRESH_DARK_HEX = '#0F7A55';
export const FRESH_TINT_HEX = '#F2FAF6';
/** Fresh zone band sub-copy — distinct from the general muted-text token. */
export const FRESH_SUBCOPY_HEX = '#5C7A6E';

export const STALE_HEX = '#F59E0B';
export const STALE_DARK_HEX = '#B45309';
export const STALE_TINT_HEX = '#FDF6EC';
/** STALE zone band sub-copy — distinct from the general muted-text token. */
export const STALE_SUBCOPY_HEX = '#9A6B3F';
/** TOUCH action chip background (fresher tint than the general stale tint). */
export const STALE_CHIP_TINT_HEX = '#FFFBF4';
/** Over-estimate meta-line chip background. */
export const OVER_ESTIMATE_CHIP_TINT_HEX = '#FDF3E7';

export const GRAVEYARD_HEX = '#A855F7';
export const GRAVEYARD_DARK_HEX = '#6B21A8';
export const GRAVEYARD_TINT_HEX = '#F6F2FA';
export const GRAVEYARD_BORDER_HEX = '#D9CBE9';
/** GRAVEYARD zone band sub-copy — distinct from the general muted-text token. */
export const GRAVEYARD_SUBCOPY_HEX = '#7A5A94';

/** WARM zone spine + the design's general dashed-border grey (same hex). */
export const WARM_SPINE_HEX = '#E0DBD1';
export const DASHED_BORDER_GREY_HEX = '#E0DBD1';
/** WARM bucket's filled meter block (parity with its age-text colour). */
export const WARM_METER_FILLED_HEX = '#A9A49A';

/** Terminal-row spine tint — closest to but distinct from ms-surface-2. */
export const TERMINAL_SPINE_HEX = '#F1EDE5';

/** 9px uppercase micro-label grey (column headers, zone band labels). */
export const MICRO_LABEL_GREY_HEX = '#7A756C';
/** Accent tint behind the active rail row and the keyboard-cursor row. */
export const ACCENT_TINT_HEX = '#FEF7F1';
/** Unfilled rot-meter block. */
export const METER_UNFILLED_HEX = '#E7E3DA';

/** Danger family — overdue DUE cell + breach SLA meta chip. */
export const DANGER_HEX = '#E11D48';
export const DANGER_STRONG_HEX = '#B3261E';
export const DANGER_DARK_HEX = '#9F1239';

// ---- Zone → spine colour (one table for the row, the rail and the band) ----

export const ZONE_SPINE_COLOR: Record<TaskRotZone, string> = {
  fresh: FRESH_HEX,
  warm: WARM_SPINE_HEX,
  stale: STALE_HEX,
  graveyard: GRAVEYARD_HEX,
  terminal: TERMINAL_SPINE_HEX,
};

/** The four zones that render a full-width zone header band; terminal never does. */
export type CompostBandZone = 'fresh' | 'warm' | 'stale' | 'graveyard';

export interface ZoneBandStyle {
  /** Uppercase zone label as it renders in the band ("FRESH", "STALE", ...). */
  label: string;
  /** Emoji glyph shown inline before the label; undefined for fresh/warm. */
  glyph?: string;
  background: string;
  labelColorHex: string;
  subCopyHex: string;
}

export const ZONE_BAND_STYLE: Record<CompostBandZone, ZoneBandStyle> = {
  fresh: {
    label: 'FRESH',
    background: FRESH_TINT_HEX,
    labelColorHex: FRESH_DARK_HEX,
    subCopyHex: FRESH_SUBCOPY_HEX,
  },
  warm: {
    label: 'WARM',
    // Handoff's warm band background is the same band tint the column
    // header uses — read the CSS variable directly so this stays one token,
    // not a duplicated raw hex.
    background: 'var(--ms-surface-2)',
    // Meaning-carrying muted text: use the ms-text-muted TOKEN (#6B6B75),
    // not the handoff's raw #8C8880 — the token is darker, satisfying the
    // handoff's own "never lighter than #8C8880" contrast rule.
    labelColorHex: 'var(--ms-text-muted)',
    subCopyHex: 'var(--ms-text-muted)',
  },
  stale: {
    label: 'STALE',
    glyph: '💩',
    background: STALE_TINT_HEX,
    labelColorHex: STALE_DARK_HEX,
    subCopyHex: STALE_SUBCOPY_HEX,
  },
  graveyard: {
    label: 'GRAVEYARD',
    glyph: '🪦',
    background: GRAVEYARD_TINT_HEX,
    labelColorHex: GRAVEYARD_DARK_HEX,
    subCopyHex: GRAVEYARD_SUBCOPY_HEX,
  },
};

/** Zone → the action chip's zone-coloured border/text (fresh reuses accent). */
export interface ZoneChipStyle {
  label: string;
  borderHex: string;
  textHex: string;
  backgroundHex?: string;
  dashed?: boolean;
}

export const ZONE_CHIP_STYLE: Record<TaskRotZone, ZoneChipStyle> = {
  fresh: { label: '+LOG', borderHex: '#FC8332', textHex: '#FC8332' },
  warm: { label: '+LOG', borderHex: '#E7E3DA', textHex: MICRO_LABEL_GREY_HEX },
  stale: {
    label: 'TOUCH',
    borderHex: STALE_HEX,
    textHex: STALE_DARK_HEX,
    backgroundHex: STALE_CHIP_TINT_HEX,
  },
  graveyard: {
    label: 'REVIVE',
    borderHex: GRAVEYARD_BORDER_HEX,
    textHex: GRAVEYARD_DARK_HEX,
  },
  terminal: {
    label: '+LOG',
    borderHex: DASHED_BORDER_GREY_HEX,
    textHex: 'var(--ms-text-muted)',
    dashed: true,
  },
};
